'use client';

import {
  FolderKanban,
  ListTodo,
  Clock,
  Target,
  DollarSign,
  TrendingUp,
  Calendar,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { getProjectStats, projects, tasks, getUpcomingMilestones } from '../data/mock-data';
import { projectStatusConfig, taskStatusConfig } from '../types';
import { formatCurrency } from '../../../types';
import type { ProjectsPage } from '../index';

interface ProjectsDashboardProps {
  onNavigate: (page: ProjectsPage) => void;
}

export function ProjectsDashboard({ onNavigate }: ProjectsDashboardProps) {
  const stats = getProjectStats();
  const upcomingMilestones = getUpcomingMilestones();

  // 활성 프로젝트
  const activeProjects = projects
    .filter((p) => p.status === 'active')
    .sort((a, b) => b.progress - a.progress);

  // 최근 작업
  const recentTasks = [...tasks]
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 5);

  const getStatusColor = (color: string) => {
    const colors: Record<string, string> = {
      gray: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400',
      blue: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      yellow: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      green: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      orange: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
      red: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    };
    return colors[color] || colors.gray;
  };

  return (
    <div className="space-y-6">
      {/* 요약 카드 */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100 dark:bg-blue-900/30">
              <FolderKanban className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">진행중 프로젝트</div>
              <div className="text-2xl font-bold">{stats.activeProjects}건</div>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-yellow-100 dark:bg-yellow-900/30">
              <ListTodo className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">진행중 작업</div>
              <div className="text-2xl font-bold">{stats.inProgressTasks}건</div>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30">
              <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">작업 완료율</div>
              <div className="text-2xl font-bold">{stats.taskCompletionRate.toFixed(1)}%</div>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30">
              <Target className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <div className="text-sm text-muted-foreground">다가오는 마일스톤</div>
              <div className="text-2xl font-bold">{stats.upcomingMilestones}건</div>
            </div>
          </div>
        </div>
      </div>

      {/* 예산 현황 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">총 예산</span>
          </div>
          <div className="text-3xl font-bold">{formatCurrency(stats.totalBudget)}</div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">실제 비용</span>
          </div>
          <div className="text-3xl font-bold">{formatCurrency(stats.totalActualCost)}</div>
        </div>

        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">예산 사용률</span>
          </div>
          <div className="text-3xl font-bold">{stats.budgetUtilization.toFixed(1)}%</div>
          <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full ${
                stats.budgetUtilization > 90
                  ? 'bg-red-500'
                  : stats.budgetUtilization > 70
                  ? 'bg-yellow-500'
                  : 'bg-green-500'
              }`}
              style={{ width: `${Math.min(stats.budgetUtilization, 100)}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 활성 프로젝트 */}
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <FolderKanban className="w-4 h-4" />
              활성 프로젝트
            </h3>
            <button
              onClick={() => onNavigate('projects')}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              전체보기 <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {activeProjects.map((project) => {
              const statusConfig = projectStatusConfig[project.status];
              return (
                <div
                  key={project.id}
                  className="p-3 rounded-lg bg-accent/50 border border-border"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-medium">{project.name}</span>
                      <div className="text-xs text-muted-foreground">{project.customerName || project.department}</div>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mb-2">
                    <span className="text-muted-foreground">{project.projectManagerName}</span>
                    <span className="font-medium">{project.progress}%</span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary rounded-full"
                      style={{ width: `${project.progress}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground mt-2">
                    <span>{project.startDate} ~ {project.endDate}</span>
                    <span>{formatCurrency(project.actualCost)} / {formatCurrency(project.budget)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 최근 작업 */}
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold flex items-center gap-2">
              <ListTodo className="w-4 h-4" />
              최근 작업
            </h3>
            <button
              onClick={() => onNavigate('tasks')}
              className="text-sm text-primary hover:underline flex items-center gap-1"
            >
              전체보기 <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          <div className="space-y-3">
            {recentTasks.map((task) => {
              const statusConfig = taskStatusConfig[task.status];
              return (
                <div
                  key={task.id}
                  className="p-3 rounded-lg bg-accent/50 border border-border"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium">{task.name}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${getStatusColor(statusConfig.color)}`}>
                      {statusConfig.label}
                    </span>
                  </div>
                  <div className="text-sm text-muted-foreground">{task.projectName}</div>
                  <div className="flex items-center justify-between text-xs mt-2">
                    <span className="text-muted-foreground">{task.assignedToName}</span>
                    <span>
                      {task.actualHours}h / {task.expectedHours}h
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* 다가오는 마일스톤 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold flex items-center gap-2">
            <Target className="w-4 h-4" />
            다가오는 마일스톤
          </h3>
        </div>
        {upcomingMilestones.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {upcomingMilestones.map((milestone) => (
              <div
                key={milestone.id}
                className="p-3 rounded-lg bg-accent/50 border border-border"
              >
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-muted-foreground">{milestone.dueDate}</span>
                </div>
                <div className="font-medium">{milestone.name}</div>
                <div className="text-sm text-muted-foreground mt-1">{milestone.projectName}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            다가오는 마일스톤이 없습니다.
          </div>
        )}
      </div>

      {/* 빠른 작업 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <h3 className="font-semibold mb-4">빠른 작업</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button
            onClick={() => onNavigate('projects')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <FolderKanban className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">프로젝트 등록</div>
          </button>
          <button
            onClick={() => onNavigate('tasks')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <ListTodo className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">작업 등록</div>
          </button>
          <button
            onClick={() => onNavigate('timesheets')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <Clock className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">타임시트 작성</div>
          </button>
          <button
            onClick={() => onNavigate('projects')}
            className="p-3 rounded-lg bg-accent hover:bg-accent/80 text-left transition-colors"
          >
            <Calendar className="w-5 h-5 mb-2 text-primary" />
            <div className="text-sm font-medium">일정 확인</div>
          </button>
        </div>
      </div>
    </div>
  );
}
